import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames/bind";
import styles from "./LiannePopup.module.scss";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getActivePopup, getWpformsHtml } from "../../services/Popup/PopupService";

const cx = classNames.bind(styles);

const getStorage = (frequency) => {
  return frequency === "session" ? window.sessionStorage : window.localStorage;
};

const getExpiryMs = (frequency) => {
  if (frequency === "day") return 24 * 60 * 60 * 1000;
  if (frequency === "week") return 7 * 24 * 60 * 60 * 1000;
  return 0;
};

const canShowPopup = (popup) => {
  const frequency = popup?.config?.frequency || "session";
  if (frequency === "always") return true;

  const key = `lianne-popup-dismissed-${popup.id}`;
  const storage = getStorage(frequency);
  const storedValue = storage.getItem(key);

  if (!storedValue) return true;
  if (frequency === "session") return false;

  const dismissedAt = Number(storedValue);
  const expiryMs = getExpiryMs(frequency);

  return !dismissedAt || Date.now() - dismissedAt > expiryMs;
};

const markPopupDismissed = (popup) => {
  const frequency = popup?.config?.frequency || "session";
  if (frequency === "always") return;

  const key = `lianne-popup-dismissed-${popup.id}`;
  const storage = getStorage(frequency);
  storage.setItem(key, String(Date.now()));
};

const getBlockText = (blocks, type, fallback = "") => {
  const block = blocks.find((item) => item.type === type && item.enabled);
  return block?.text || block?.config?.text || fallback;
};

const getWpformsBlockId = (blocks, fallback = 0) => {
  const block = blocks.find(
    (item) => item.type === "wpforms" && (item.enabled !== false) && (item.formId || item.config?.formId)
  );
  return Number(block?.formId || block?.config?.formId || fallback || 0);
};

// v2: get image block (first image block in blocks array)
const getImageBlock = (blocks) => {
  return blocks.find((b) => b.type === "image" && (b.enabled !== false));
};

const hasVisibleBlockContent = (block, wpformsId) => {
  if (block.enabled === false) return false;
  const cfg = block.config || block;
  if (["eyebrow", "heading", "description"].includes(block.type)) return Boolean(cfg.text || block.text);
  if (block.type === "button") return Boolean((cfg.text || block.text) && (cfg.url || block.url));
  if (block.type === "wpforms") return wpformsId > 0;
  if (block.type === "divider") return true;
  if (block.type === "countdown") return Boolean(cfg.endDate || cfg.label || block.endDate || block.label);
  if (block.type === "coupon") return Boolean(cfg.code || block.code);
  if (block.type === "social") return Boolean(cfg.facebook || cfg.instagram || cfg.tiktok || cfg.youtube || block.facebook);
  return false;
};

// v2 layout helpers
const isV2Layout = (layout) => layout && typeof layout === "object" && "columns" in layout;

const getV2LayoutClass = (layout) => {
  if (!isV2Layout(layout)) return null;
  const cols = layout.columns || 1;
  if (cols === 1) return "columns-1";
  if (cols === 2) return "columns-2";
  if (cols === 3) return "columns-3";
  return null;
};

// Compute CSS grid-template-columns string from v2 layout
const getColumnTemplate = (layout) => {
  if (!isV2Layout(layout)) return null;
  const widths = layout.columnWidths;
  if (!Array.isArray(widths) || widths.length < 2) return null;
  return widths.map((w) => `${w}fr`).join(" ");
};

const getCountdownRemaining = (endDate, now) => {
  const endTime = new Date(endDate).getTime();
  if (!Number.isFinite(endTime)) return 0;
  return Math.max(0, endTime - now);
};

const getCountdownParts = (remainingMs) => {
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
};

const padTime = (value) => String(value).padStart(2, "0");

const matchesPageTarget = (target, pathname) => {
  if (!target || target === "all") return true;
  if (target === "home") return pathname === "/";
  if (target === "product") return pathname.startsWith("/product-detail/");
  if (target === "category") return pathname.startsWith("/products/category") || pathname === "/gifts";
  if (target === "checkout") return pathname.startsWith("/checkout") || pathname === "/final-payment";
  if (target === "profile") return pathname === "/profile";

  return true;
};

const matchesDeviceTarget = (device) => {
  if (!device || device === "all") return true;

  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  return device === "mobile" ? isMobile : !isMobile;
};

const LiannePopup = () => {
  const { pathname } = useLocation();
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formHtml, setFormHtml] = useState("");
  const [now, setNow] = useState(Date.now());

  const handleClose = useCallback(() => {
    if (popup) {
      markPopupDismissed(popup);
    }
    setIsVisible(false);
  }, [popup]);

  useEffect(() => {
    let isMounted = true;
    let timerId;

    const fetchPopup = async () => {
      const activePopup = await getActivePopup();
      const config = activePopup?.config;

      if (!isMounted) {
        return;
      }

      if (
        !config?.enabled ||
        !matchesPageTarget(config.target, pathname) ||
        !matchesDeviceTarget(config.device) ||
        !canShowPopup(activePopup)
      ) {
        setPopup(null);
        setIsVisible(false);
        return;
      }

      setPopup(activePopup);
      timerId = window.setTimeout(() => {
        if (isMounted) setIsVisible(true);
      }, Number(activePopup.config.delay) || 0);
    };

    fetchPopup();

    return () => {
      isMounted = false;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [pathname]);

  useEffect(() => {
    const blocks = popup?.config?.blocks || [];
    const wpformsId = getWpformsBlockId(blocks, popup?.config?.wpformsId);

    if (!wpformsId || !isVisible) {
      setFormHtml("");
      return;
    }

    let isMounted = true;

    const fetchForm = async () => {
      const response = await getWpformsHtml(wpformsId);
      if (!isMounted) return;
      setFormHtml(response?.html || "");
    };

    fetchForm();

    return () => {
      isMounted = false;
    };
  }, [popup, isVisible]);

  useEffect(() => {
    if (!formHtml) return;

    window.setTimeout(() => {
      if (window.wpforms?.ready) {
        window.wpforms.ready();
      }
    }, 0);
  }, [formHtml]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, handleClose]);

  const config = popup?.config;
  const googleFontsUrl = config?.googleFontsUrl;

  useEffect(() => {
    const fontUrl = googleFontsUrl;
    if (!fontUrl) return;

    const id = "lianne-popup-google-fonts";
    const existingLink = document.getElementById(id);
    if (existingLink?.getAttribute("href") === fontUrl) {
      return;
    }

    if (existingLink) {
      existingLink.remove();
    }

    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = fontUrl;
    document.head.appendChild(link);
  }, [googleFontsUrl]);

  // Inject custom CSS từ config vào <style> tag
  const customCss = config?.customCss;
  useEffect(() => {
    const styleId = "lianne-popup-custom-css";
    const existingStyle = document.getElementById(styleId);

    if (!customCss) {
      if (existingStyle) existingStyle.remove();
      return;
    }

    if (existingStyle) {
      existingStyle.textContent = customCss;
    } else {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = customCss;
      document.head.appendChild(style);
    }

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [customCss]);

  const blocks = useMemo(() => {
    if (!config?.blocks?.length) {
      return [
        { type: "eyebrow", enabled: Boolean(config?.eyebrow), text: config?.eyebrow || "" },
        { type: "heading", enabled: Boolean(config?.heading), text: config?.heading || "" },
        { type: "description", enabled: Boolean(config?.description), text: config?.description || "" },
        {
          type: "button",
          enabled: Boolean(config?.buttonText && config?.buttonUrl),
          text: config?.buttonText || "",
          url: config?.buttonUrl || "",
        },
        { type: "wpforms", enabled: Boolean(config?.wpformsId), formId: config?.wpformsId || 0 },
      ];
    }

    return config.blocks;
  }, [config]);

  // v2: image lives in blocks[], v1: config.imageUrl
  const imageBlock = useMemo(() => getImageBlock(blocks), [blocks]);
  const hasImage = Boolean(imageBlock?.config?.imageUrl || config?.imageUrl);
  const imageUrl = imageBlock?.config?.imageUrl || config?.imageUrl || "";
  const imageLink = imageBlock?.config?.imageLink || config?.imageLink || "";

  // v2 layout
  const layout = config?.layout;
  const v2Layout = isV2Layout(layout);
  const columnTemplate = v2Layout ? getColumnTemplate(layout) : null;
  const columnGap = v2Layout ? (layout.gap ?? 0) : 0;
  const numColumns = v2Layout ? (layout.columns || 1) : 1;
  const verticalAligns = v2Layout ? (layout.verticalAlign || []) : [];

  const heading = getBlockText(blocks, "heading", config?.heading || "");
  const wpformsId = getWpformsBlockId(blocks, config?.wpformsId);
  const hasContent = blocks.some((block) => hasVisibleBlockContent(block, wpformsId));
  const shouldHideExpiredPopup = blocks.some((block) => {
    if (block.enabled === false || block.type !== "countdown") return false;
    const cfg = block.config || block;
    const expiredAction = cfg.expiredAction || block.expiredAction;
    const endDate = cfg.endDate || block.endDate;
    if (expiredAction !== "hide" || !endDate) return false;
    return getCountdownRemaining(endDate, now) <= 0;
  });

  useEffect(() => {
    const hasCountdown = blocks.some((block) => block.enabled && block.type === "countdown" && block.endDate);
    if (!isVisible || !hasCountdown) return;

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [blocks, isVisible]);

  const popupClasses = useMemo(() => {
    if (!config) return "";

    // v2: layout is object → use columns-N class
    // v1: layout is string → use legacy class
    const layoutClass = v2Layout
      ? getV2LayoutClass(config.layout)
      : `popup--${config.layout}`;

    return cx(
      "popup",
      layoutClass,
      `popup--${config.theme}`,
      `popup--${config.position}`,
      `popup--form-align-${config.formAlign || "left"}`,
      `popup--form-width-${config.formWidth || "normal"}`,
      `popup--form-button-${config.formButton || "auto"}`,
      `popup--anim-${config.resolvedEffects?.animation || popup?.resolvedEffects?.animation || "fade"}`,
      `popup--overlay-${config.resolvedEffects?.imageOverlay || popup?.resolvedEffects?.imageOverlay || "none"}`,
      {
        "popup--no-image": !hasImage,
        "popup--image-only": hasImage && !hasContent,
        "popup--with-form": Boolean(wpformsId),
      }
    );
  }, [config, popup, hasImage, hasContent, wpformsId, v2Layout]);

  // Biến cho overlay (cha) — chỉ backdrop
  const overlayVariables = useMemo(() => {
    const effects = config?.resolvedEffects || popup?.resolvedEffects;
    const vars = {};
    if (effects) {
      vars["--popup-backdrop"] = effects.backdropColor;
      vars["--popup-backdrop-filter"] = effects.backdropFilter;
    }
    return vars;
  }, [config, popup]);

  // Biến cho popup (con) — colors, typography, sizing, effects khác
  // Phải set trực tiếp trên .popup vì SCSS default (--popup-padding: 42px 38px)
  // trên .popup override giá trị inherited từ .overlay
  const popupVariables = useMemo(() => {
    const colors = config?.resolvedColors || popup?.resolvedColors;
    const typography = config?.resolvedTypography || popup?.resolvedTypography;
    const sizing = config?.resolvedSizing || popup?.resolvedSizing;
    const effects = config?.resolvedEffects || popup?.resolvedEffects;
    const variables = {};

    if (colors) {
      variables["--popup-bg"] = colors.background;
      variables["--popup-text"] = colors.text;
      variables["--popup-muted"] = colors.muted;
      variables["--popup-heading"] = colors.heading;
      variables["--popup-accent"] = colors.accent;
      variables["--popup-accent-text"] = colors.accentText;
      variables["--popup-border"] = colors.border;
      if (colors.inputBorder) variables["--popup-input-border"] = colors.inputBorder;
      if (colors.closeBtnColor) variables["--popup-close-btn"] = colors.closeBtnColor;
    }

    if (typography) {
      variables["--popup-heading-font"] = `"${typography.headingFont}", var(--primary-font)`;
      variables["--popup-body-font"] = `"${typography.bodyFont}", var(--second-font)`;
      variables["--popup-heading-size"] = typography.headingSize;
      variables["--popup-body-size"] = typography.bodySize;
      variables["--popup-eyebrow-size"] = typography.eyebrowSize;
    }

    if (sizing) {
      variables["--popup-width"] = `${sizing.popupWidth}px`;
      variables["--popup-padding"] = sizing.contentPadding;
      variables["--popup-radius"] = sizing.borderRadius;
      variables["--popup-image-ratio"] = sizing.imageRatio;
      if (sizing.blockGap) variables["--popup-block-gap"] = sizing.blockGap;
      if (sizing.fieldGap) variables["--popup-field-gap"] = sizing.fieldGap;
    }

    if (effects) {
      variables["--popup-shadow"] = effects.shadowValue;
      variables["--popup-animation-duration"] = `${effects.durationMs}ms`;
    }

    return variables;
  }, [config, popup]);

  const renderBlock = (block, index) => {
    if (block.enabled === false) return null;
    // v2: config fields live under block.config; v1: directly on block
    const cfg = block.config || block;
    const text = cfg.text || block.text || "";
    const url = cfg.url || block.url || "";

    if (block.type === "eyebrow" && text) {
      return <p key={`${block.id || block.type}-${index}`} className={`${cx("eyebrow")} popup-eyebrow`}>{text}</p>;
    }

    if (block.type === "heading" && text) {
      return <h2 key={`${block.id || block.type}-${index}`} className={`${cx("heading")} popup-heading`}>{text}</h2>;
    }

    if (block.type === "description" && text) {
      return <p key={`${block.id || block.type}-${index}`} className={`${cx("description")} popup-description`}>{text}</p>;
    }

    if (block.type === "button" && text && url) {
      return (
        <a key={`${block.id || block.type}-${index}`} className={cx("cta")} href={url}>
          {text}
        </a>
      );
    }

    if (block.type === "wpforms" && wpformsId > 0) {
      return (
        <div key={`${block.id || block.type}-${index}`} className={cx("form")}>
          {formHtml ? (
            <div dangerouslySetInnerHTML={{ __html: formHtml }} />
          ) : (
            <div className={cx("form-loading")}>Loading form...</div>
          )}
        </div>
      );
    }

    if (block.type === "divider") {
      return <div key={`${block.id || block.type}-${index}`} className={cx("divider", `divider--${cfg.style || block.style || "line"}`)} />;
    }

    const endDate = cfg.endDate || block.endDate;
    const label = cfg.label || block.label;
    if (block.type === "countdown" && (endDate || label)) {
      const remainingMs = getCountdownRemaining(endDate, now);
      const isExpired = endDate && remainingMs <= 0;
      const expiredAction = cfg.expiredAction || block.expiredAction;
      const expiredText = cfg.expiredText || block.expiredText;

      if (isExpired && expiredAction === "show-text") {
        return (
          <div key={`${block.id || block.type}-${index}`} className={cx("countdown-expired")}>
            {expiredText || "Chương trình đã kết thúc"}
          </div>
        );
      }

      if (isExpired) return null;

      const parts = getCountdownParts(remainingMs);
      const items = [
        ["Days", parts.days],
        ["Hrs", parts.hours],
        ["Min", parts.minutes],
        ["Sec", parts.seconds],
      ];

      return (
        <div key={`${block.id || block.type}-${index}`} className={cx("countdown", `countdown--${cfg.style || block.style || "boxes"}`)}>
          {label && <div className={cx("countdown-label")}>{label}</div>}
          <div className={cx("countdown-values")}>
            {items.map(([lbl, value]) => (
              <span key={lbl}>
                <strong>{lbl === "Days" ? value : padTime(value)}</strong>
                <small>{lbl}</small>
              </span>
            ))}
          </div>
        </div>
      );
    }

    const couponCode = cfg.code || block.code;
    if (block.type === "coupon" && couponCode) {
      const couponLabel = cfg.label || block.label;
      const handleCopy = async () => {
        await navigator.clipboard?.writeText(couponCode);
        toast.success("Đã sao chép mã giảm giá");
      };

      return (
        <div key={`${block.id || block.type}-${index}`} className={cx("coupon")}>
          {couponLabel && <span className={cx("coupon-label")}>{couponLabel}</span>}
          <button className={cx("coupon-code")} type="button" onClick={handleCopy}>
            <span>{couponCode}</span>
            <b>Copy</b>
          </button>
        </div>
      );
    }

    if (block.type === "social") {
      const links = [
        ["Facebook", "f", cfg.facebook || block.facebook],
        ["Instagram", "ig", cfg.instagram || block.instagram],
        ["TikTok", "tt", cfg.tiktok || block.tiktok],
        ["YouTube", "yt", cfg.youtube || block.youtube],
      ].filter(([, , u]) => Boolean(u));

      if (!links.length) return null;

      return (
        <div key={`${block.id || block.type}-${index}`} className={cx("social", `social--${cfg.style || block.style || "icons"}`)}>
          {links.map(([lbl, icon, u]) => (
            <a key={lbl} href={u} target="_blank" rel="noopener noreferrer">
              <b>{icon}</b>
              <em>{lbl}</em>
            </a>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderBadgeBlock = (block, index) => {
    if (!block.enabled || block.type !== "badge" || !block.text) return null;

    const style = block.color ? { "--popup-badge-color": block.color } : undefined;

    return (
      <span
        key={`${block.type}-${index}`}
        className={cx("badge", `badge--${block.position || "top-left"}`, `badge--${block.style || "ribbon"}`)}
        style={style}
      >
        {block.text}
      </span>
    );
  };

  if (!popup || !config || !isVisible || shouldHideExpiredPopup) {
    return null;
  }

  // v2: group blocks by column index, render as grid columns
  const renderV2Content = () => {
    // Build columns: array of arrays indexed by column number
    const columnArrays = Array.from({ length: numColumns }, () => []);
    blocks.forEach((block, idx) => {
      if (block.type === "image" || block.type === "badge") return; // handled separately
      const colIdx = Math.min(block.column ?? 0, numColumns - 1);
      columnArrays[colIdx].push({ block, idx });
    });

    const gridStyle = {
      display: "grid",
      gridTemplateColumns: columnTemplate || "1fr",
      gap: `${columnGap}px`,
      width: "100%",
      minHeight: 0,
    };

    return (
      <div style={gridStyle}>
        {columnArrays.map((colBlocks, colIdx) => {
          const valign = verticalAligns[colIdx] || "center";
          const alignItems = valign === "top" ? "flex-start" : valign === "bottom" ? "flex-end" : valign === "stretch" ? "stretch" : "center";

          // Check if this column is an image-only column
          const imageBlockForCol = blocks.find(
            (b) => b.type === "image" && (b.enabled !== false) && (b.column ?? 0) === colIdx
          );

          if (imageBlockForCol) {
            const imgCfg = imageBlockForCol.config || imageBlockForCol;
            const imgSrc = imgCfg.imageUrl || "";
            const imgHref = imgCfg.imageLink || "";
            const objFit = imgCfg.objectFit || "cover";
            return (
              <div
                key={colIdx}
                className={`${cx("media")} popup-media`}
                style={{ alignSelf: alignItems, minHeight: "100%" }}
              >
                {imgHref ? (
                  <a href={imgHref} target="_blank" rel="noopener noreferrer" className={cx("media-link")}>
                    <img src={imgSrc} alt={heading || popup.title || ""} loading="lazy" style={{ objectFit: objFit, width: "100%", height: "100%", display: "block", minHeight: "280px" }} />
                  </a>
                ) : (
                  <img src={imgSrc} alt={heading || popup.title || ""} loading="lazy" style={{ objectFit: objFit, width: "100%", height: "100%", display: "block", minHeight: "280px" }} />
                )}
                {colBlocks.map(({ block, idx }) => renderBlock(block, idx))}
              </div>
            );
          }

          if (!colBlocks.length) return null;

          return (
            <div
              key={colIdx}
              className={cx("content")}
              style={{ alignSelf: alignItems }}
            >
              <div className={cx("copy")}>
                {colBlocks.map(({ block, idx }) => renderBlock(block, idx))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // v1 legacy render
  const renderV1Content = () => (
    <>
      {hasImage && (
        <div className={`${cx("media")} popup-media`}>
          {imageLink ? (
            <a href={imageLink} target="_blank" rel="noopener noreferrer" className={cx("media-link")}>
              <img src={imageUrl} alt={heading || popup.title || ""} loading="lazy" />
            </a>
          ) : (
            <img src={imageUrl} alt={heading || popup.title || ""} loading="lazy" />
          )}
        </div>
      )}
      {hasContent && (
        <div className={cx("content")}>
          <div className={cx("copy")}>
            {blocks.map(renderBlock)}
          </div>
        </div>
      )}
    </>
  );

  // v2 popup style: override grid via inline style when using columns layout
  const popupInlineStyle = v2Layout && columnTemplate
    ? { ...popupVariables, display: "flex", flexDirection: "column", maxWidth: `min(92vw, ${(config?.resolvedSizing?.popupWidth || 900)}px)`, width: "100%" }
    : popupVariables;

  return (
    <div
      className={cx("overlay", `overlay--${config.position}`)}
      style={overlayVariables}
      onMouseDown={handleClose}
    >
      <section
        className={`${popupClasses} lianne-popup`}
        style={popupInlineStyle}
        role="dialog"
        aria-modal="true"
        aria-label={heading || popup.title || "Popup"}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className={`${cx("close-button")} popup-close`} type="button" onClick={handleClose} aria-label="Close popup">
          <X size={18} />
        </button>

        {blocks.map(renderBadgeBlock)}

        {v2Layout ? renderV2Content() : renderV1Content()}
      </section>
    </div>
  );
};

export default LiannePopup;
